import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { vaultTokens } from '@/utils/api';
import { ShieldCheck, Lock, Check, Zap, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function VaultSubmit() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [value, setValue] = useState('');
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    vaultTokens.publicGet(token).then(r => setData(r.data)).catch(e => setData({ error: e.response?.data?.detail || 'Link invalid' }));
  }, [token]);

  const submit = async () => {
    if (!value.trim()) return toast.error('Enter the value');
    setBusy(true);
    try {
      // If a username was provided, combine as JSON so the vault entry holds both
      const payload = username.trim() ? JSON.stringify({ username: username.trim(), password: value }) : value;
      await vaultTokens.publicSubmit(token, payload);
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAF8F4' }}><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#006080', borderTopColor: 'transparent' }} /></div>;

  if (data.error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
        <div className="max-w-md text-center p-8">
          <AlertCircle size={48} style={{ color: '#B34A3A', margin: '0 auto 16px' }} />
          <h1 className="text-2xl font-black mb-2" style={{ color: '#1A2428' }}>Link unavailable</h1>
          <p className="text-sm" style={{ color: '#5C6B72' }}>{data.error}</p>
          <p className="text-xs mt-3" style={{ color: '#5C6B72' }}>Secure links are single-use and expire in 48 hours for your protection.</p>
        </div>
      </div>
    );
  }

  const brand = data.brand;
  const color = brand?.primary_color || '#006080';

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
        <div className="max-w-md text-center p-8">
          <Check size={48} style={{ color, margin: '0 auto 16px' }} />
          <h1 className="text-2xl font-black mb-2" style={{ color: '#1A2428' }}>Encrypted & stored</h1>
          <p className="text-sm" style={{ color: '#5C6B72' }}>This link has now been burned. You can safely close this tab.</p>
        </div>
      </div>
    );
  }

  const isLogin = data.type === 'login';

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: color }}>
            <Zap size={14} color="white" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-base" style={{ color: '#1A2428' }}>{brand?.name || 'Agency'}</span>
        </div>

        <div className="rounded-card p-6" style={{ background: 'white', border: '1px solid #E8E3D9' }}>
          <div className="flex items-start gap-3 mb-4">
            <ShieldCheck size={24} style={{ color }} />
            <div>
              <h1 className="text-xl font-black" style={{ color: '#1A2428' }}>Secure credential submission</h1>
              <p className="text-xs mt-0.5" style={{ color: '#5C6B72' }}>End-to-end encrypted · single-use · expires in 48 hours</p>
            </div>
          </div>

          <div className="p-3 rounded-btn mb-4" style={{ background: '#E6F1F4' }}>
            <p className="text-xs font-semibold" style={{ color }}>For:</p>
            <p className="text-sm font-bold mt-0.5" style={{ color: '#1A2428' }} data-testid="vault-submit-label">{data.label}</p>
            {data.notes && <p className="text-xs mt-1" style={{ color: '#5C6B72' }}>{data.notes}</p>}
          </div>

          <div className="space-y-3">
            {isLogin && (
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: '#5C6B72' }}>Username / Email</label>
                <input className="input-agency text-sm" value={username} onChange={e => setUsername(e.target.value)} autoComplete="off" data-testid="vault-submit-username" />
              </div>
            )}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: '#5C6B72' }}>
                <Lock size={10} /> {data.type === 'api_key' ? 'API key' : data.type === 'note' ? 'Secret note' : 'Password'}
              </label>
              <textarea className="input-agency text-sm resize-none" rows={3} value={value} onChange={e => setValue(e.target.value)} autoComplete="off" autoCorrect="off" spellCheck="false" data-testid="vault-submit-value" />
            </div>
          </div>

          <button type="button" className="w-full mt-4 py-3 rounded-btn text-white font-semibold text-sm" style={{ background: color }} onClick={submit} disabled={busy || !value.trim()} data-testid="vault-submit-button">
            {busy ? 'Encrypting & storing…' : 'Submit securely'}
          </button>

          <p className="text-xs mt-3 text-center" style={{ color: '#5C6B72' }}>Your submission is AES-256 encrypted immediately on our server. The link burns after one successful submission.</p>
        </div>
      </div>
    </div>
  );
}
